/**
 * 레이지 무브 — 사용자 피드백("한번에 쓸어버리거나 책상을 뒤집을수도 있으면").
 *  - sweep(쓸어버리기): 팔(RageGestures가 위치 주입)이 지나간 물건에만 팔 이동속도에
 *    비례한 속도를 전달하는 연속 배팅. 파괴는 직접 하지 않고 벽/바닥 충돌(DestructionSystem)에
 *    맡긴다 → 살살 쓸면 살짝 밀리고, 세게 쓸면 튕겨나가 깨진다(점수·콤보·사운드는 평소 경로).
 *  - flip(책상 뒤집기): desk:destroyed(flip)를 발행해 상판을 통째로 뒤집고(DeskBreak),
 *    물건 전부를 공중으로 쏘아올린다 — 천장 충돌은 즉시 파괴라 피날레가 완성된다.
 * 두 무브 모두 잡은(grabbed) 물건은 건드리지 않는다(kinematic — 속도 지정이 무의미).
 * 쿨다운/세션당 1회 같은 사용 제한은 UI(RageDock)가 소유한다.
 */
import type { EventBus } from '../core/EventBus';
import type { ObjectSpawner } from './ObjectSpawner';
import { BALANCE } from '../config/balance';
import { LAYOUT } from '../config/layout';
import { sweepBatVelocity, flipLaunchVelocity, tumbleSpin, type SweepDir } from './rageLogic';

export class RageMoves {
  private deskGone = false;

  // --- 연속 쓸기 배팅 상태 ---
  private sweepActive = false;
  private sweepDir: SweepDir = 1;
  /** RageGestures가 매 포인터 이동마다 갱신하는 손 X(월드). */
  private sweepHandX = 0;
  /** 직전 스텝 손 X(크로싱 구간 계산). null이면 첫 스텝. */
  private sweepPrevX: number | null = null;
  private sweepLastMs = 0;
  /** 이번 쓸기에서 이미 쳐낸 instanceId(재-배팅으로 손에 들러붙는 것 방지). */
  private sweepBatted = new Set<number>();
  /** 첫 배팅 시 rage:sweep 1회 발행(우시·셰이크·쿨다운 트리거)했는지. */
  private sweepEmitted = false;

  constructor(
    private spawner: ObjectSpawner,
    private bus: EventBus,
  ) {
    const reset = (): void => {
      this.deskGone = false;
      this.sweepActive = false;
    };
    this.bus.on('session:start', reset);
    this.bus.on('session:reset', reset);
    // 자체 flip이든 내리치기 반파든 — 책상이 사라지면 뒤집기는 더 이상 불가.
    this.bus.on('desk:destroyed', () => {
      this.deskGone = true;
    });
  }

  /** 책상이 아직 있어서 뒤집을 수 있는가(RageDock 버튼 활성 판단). */
  get canFlip(): boolean {
    return !this.deskGone;
  }

  /** 쓸기 시작 — dir 방향, 손 시작 X. */
  beginSweep(dir: SweepDir, handX: number): void {
    this.sweepActive = true;
    this.sweepDir = dir;
    this.sweepHandX = handX;
    this.sweepPrevX = null;
    this.sweepBatted.clear();
    this.sweepEmitted = false;
  }

  /** 손 X 갱신(RageGestures.onMove). */
  setSweepHand(handX: number): void {
    this.sweepHandX = handX;
  }

  /**
   * 고정 스텝마다(물리 스텝 직전) 호출. 직전 스텝→이번 스텝 사이 손이 지나간 X 구간의
   * 미배팅 물건을 팔 이동속도에 비례해 쳐낸다. 팔 속도는 위치 델타 / 실경과시간으로 구한다
   * (checkGrabbedDeskSmash와 동일 이유 — 슬로모/저fps에서 게임시간 dt로 나누면 왜곡됨).
   */
  stepSweep(): void {
    if (!this.sweepActive) return;
    const nowMs = performance.now();
    const cur = this.sweepHandX;
    if (this.sweepPrevX === null) {
      this.sweepPrevX = cur;
      this.sweepLastMs = nowMs;
      return;
    }
    const realDt = Math.max(0.008, (nowMs - this.sweepLastMs) / 1000);
    const cfg = BALANCE.rage.sweep;
    const armSpeed = Math.min(cfg.maxArmSpeed, Math.abs(cur - this.sweepPrevX) / realDt);
    const lo = Math.min(this.sweepPrevX, cur) - cfg.reach;
    const hi = Math.max(this.sweepPrevX, cur) + cfg.reach;
    this.sweepPrevX = cur;
    this.sweepLastMs = nowMs;
    if (armSpeed < cfg.minArmSpeed) return; // 정지/미세 이동은 배팅 안 함

    const spin = Math.min(cfg.spin, armSpeed * cfg.spinRatio);
    for (const obj of this.spawner.objects.values()) {
      if (obj.state === 'broken' || obj.state === 'grabbed') continue;
      if (this.sweepBatted.has(obj.instanceId)) continue;
      const t = obj.body.translation();
      if (t.x < lo || t.x > hi) continue; // 이번 스텝에 손이 지나간 X 구간 밖
      obj.body.setLinvel(sweepBatVelocity(this.sweepDir, armSpeed, Math.random), true);
      obj.body.setAngvel(tumbleSpin(spin, Math.random), true);
      obj.state = 'thrown';
      this.sweepBatted.add(obj.instanceId);
      if (!this.sweepEmitted) {
        this.sweepEmitted = true;
        this.bus.emit('rage:sweep', { direction: this.sweepDir });
      }
    }
  }

  /** 쓸기 종료. 하나라도 쳐냈으면 true(쿨다운 소비/모드 해제 판단). */
  endSweep(): boolean {
    const batted = this.sweepBatted.size > 0;
    this.sweepActive = false;
    this.sweepPrevX = null;
    return batted;
  }

  /** 검증용: 제스처 없이 지정 armSpeed로 전 물건을 즉시 쳐낸다(살살/세게 비교). */
  debugSweep(dir: SweepDir, armSpeed: number): void {
    const cfg = BALANCE.rage.sweep;
    const spin = Math.min(cfg.spin, armSpeed * cfg.spinRatio);
    for (const obj of this.spawner.objects.values()) {
      if (obj.state === 'broken' || obj.state === 'grabbed') continue;
      obj.body.setLinvel(sweepBatVelocity(dir, armSpeed, Math.random), true);
      obj.body.setAngvel(tumbleSpin(spin, Math.random), true);
      obj.state = 'thrown';
    }
    this.bus.emit('rage:sweep', { direction: dir });
  }

  /** 책상 뒤집기(세션당 1회 — 책상이 사라지면 무효). */
  flip(): void {
    if (this.deskGone) return;
    // DeskBreak(상판 뒤집기)·AudioBus(붕괴음)·ScreenShake(최대 트라우마)·DeskCracks(정리)가
    // 모두 이 이벤트에 반응한다. flip.up이 물건 발사 속도이자 연출 강도.
    this.bus.emit('desk:destroyed', {
      position: { x: 0, y: LAYOUT.desk.topY, z: 0 },
      impactSpeed: BALANCE.rage.flip.up,
      flip: true,
    });
    for (const obj of this.spawner.objects.values()) {
      if (obj.state === 'broken' || obj.state === 'grabbed') continue;
      obj.body.setLinvel(flipLaunchVelocity(Math.random), true);
      obj.body.setAngvel(tumbleSpin(BALANCE.rage.flip.spin, Math.random), true);
      obj.state = 'thrown';
    }
  }
}
